import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../context/AuthContext';

interface Resident {
  id: string;
  email: string;
  username: string;
}

interface InvoiceModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSubmit: (invoiceData: any) => void;
}

const InvoiceModal: React.FC<InvoiceModalProps> = ({ isOpen, onClose, onSubmit }) => {
  const { user } = useAuth();
  const [invoiceType, setInvoiceType] = useState('monthly');
  const [amount, setAmount] = useState('250.00');
  const [description, setDescription] = useState('Monthly Rent');
  const [dueDate, setDueDate] = useState('');
  const [selectedResident, setSelectedResident] = useState('');
  const [residents, setResidents] = useState<Resident[]>([]);
  const [loading, setLoading] = useState(false);
  const [isRecurring, setIsRecurring] = useState(false);
  const [recurringDayOfMonth, setRecurringDayOfMonth] = useState(1);

  const fetchResidents = useCallback(async () => {
    try {
      const groupId = user?.hoaId || user?.propertyGroupId;
      const response = await fetch(`/api/users/residents/${groupId}`, {
        headers: {
          'Authorization': `Bearer ${user?.token}`
        }
      });

      if (response.ok) {
        const data = await response.json();
        setResidents(data);
      }
          } catch (error) {
        console.error('Error fetching residents:', error);
      }
    }, [user?.hoaId, user?.propertyGroupId, user?.token]);

  useEffect(() => {
    if (isOpen && (user?.hoaId || user?.propertyGroupId)) {
      // Set default due date (30 days from now)
      const defaultDueDate = new Date();
      defaultDueDate.setDate(defaultDueDate.getDate() + 30);
      setDueDate(defaultDueDate.toISOString().split('T')[0]);

      // Fetch residents for specific invoice option
      fetchResidents();
    }
  }, [isOpen, user?.hoaId, user?.propertyGroupId, fetchResidents]);

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);

    const invoiceData: any = {
      hoaId: user?.hoaId || user?.propertyGroupId,
      amount: parseFloat(amount),
      description,
      dueDate: new Date(dueDate).toISOString(),
      isRecurring,
      recurringDayOfMonth: isRecurring ? recurringDayOfMonth : undefined
    };

    if (invoiceType === 'specific' && selectedResident) {
      invoiceData.specificUserId = selectedResident;
    } else {
      invoiceData.createForAllResidents = true;
    }

    onSubmit(invoiceData);
    setLoading(false);
  };

  const handleTypeChange = (type: string) => {
    setInvoiceType(type);
    
    // Update default values based on type
    if (type === 'monthly') {
      setAmount('250.00');
      setDescription('Monthly Rent');
      const dueDate = new Date();
      dueDate.setDate(dueDate.getDate() + 30);
      setDueDate(dueDate.toISOString().split('T')[0]);
    } else if (type === 'assessment') {
      setAmount('500.00');
      setDescription('Special Assessment');
      const dueDate = new Date();
      dueDate.setDate(dueDate.getDate() + 60);
      setDueDate(dueDate.toISOString().split('T')[0]);
    } else if (type === 'specific') {
      setAmount('100.00');
      setDescription('Custom Invoice');
      const dueDate = new Date();
      dueDate.setDate(dueDate.getDate() + 30);
      setDueDate(dueDate.toISOString().split('T')[0]);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg p-6 w-full max-w-md mx-4">
        <div className="flex justify-between items-center mb-4">
          <h2 className="text-xl font-bold text-gray-800">Create Invoice</h2>
          <button
            onClick={onClose}
            className="text-gray-500 hover:text-gray-700"
          >
            ✕
          </button>
        </div>

        <form onSubmit={handleSubmit} className="space-y-4">
          {/* Invoice Type */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Invoice Type</label>
            <div className="space-y-2">
              <label className="flex items-center">
                <input
                  type="radio"
                  name="invoiceType"
                  value="monthly"
                  checked={invoiceType === 'monthly'}
                  onChange={(e) => handleTypeChange(e.target.value)}
                  className="mr-2"
                />
                <span className="text-gray-700">Monthly dues for all residents</span>
              </label>
              <label className="flex items-center">
                <input
                  type="radio"
                  name="invoiceType"
                  value="assessment"
                  checked={invoiceType === 'assessment'}
                  onChange={(e) => handleTypeChange(e.target.value)}
                  className="mr-2"
                />
                <span className="text-gray-700">Special assessment for all residents</span>
              </label>
              <label className="flex items-center">
                <input
                  type="radio"
                  name="invoiceType"
                  value="specific"
                  checked={invoiceType === 'specific'}
                  onChange={(e) => handleTypeChange(e.target.value)}
                  className="mr-2"
                />
                <span className="text-gray-700">Custom invoice for specific resident</span>
              </label>
            </div>
          </div>

          {/* Resident Selection (only for specific invoices) */}
          {invoiceType === 'specific' && (
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Select Resident</label>
              <select
                value={selectedResident}
                onChange={(e) => setSelectedResident(e.target.value)}
                required
                className="w-full p-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500 text-gray-800"
              >
                <option value="">Choose a resident...</option>
                {residents.map((resident) => (
                  <option key={resident.id} value={resident.id}>
                    {resident.username} ({resident.email})
                  </option>
                ))}
              </select>
            </div>
          )}

          {/* Amount */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Amount ($)</label>
            <input
              type="number"
              step="0.01"
              value={amount}
              onChange={(e) => setAmount(e.target.value)}
              required
              className="w-full p-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500 text-gray-800"
            />
          </div>

          {/* Description */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Description</label>
            <input
              type="text"
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              required
              className="w-full p-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500 text-gray-800"
            />
          </div>

          {/* Due Date */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Due Date</label>
            <input
              type="date"
              value={dueDate}
              onChange={(e) => setDueDate(e.target.value)}
              required
              className="w-full p-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500 text-gray-800"
            />
          </div>

          {/* Recurring Option */}
          <div className="bg-gray-50 p-4 rounded-lg border border-gray-200">
            <label className="flex items-center cursor-pointer">
              <input
                type="checkbox"
                checked={isRecurring}
                onChange={(e) => setIsRecurring(e.target.checked)}
                className="w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
              />
              <span className="ml-2 text-sm font-medium text-gray-700">Make this a recurring invoice</span>
            </label>
            
            {isRecurring && (
              <div className="mt-3 space-y-3">
                <p className="text-xs text-gray-500">
                  This will create a Stripe subscription to automatically charge the resident each month.
                </p>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Day of Month to Charge</label>
                  <select
                    value={recurringDayOfMonth}
                    onChange={(e) => setRecurringDayOfMonth(parseInt(e.target.value))}
                    className="w-full p-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500 text-gray-800"
                  >
                    {[...Array(31)].map((_, i) => (
                      <option key={i + 1} value={i + 1}>
                        {i + 1}{i === 0 ? 'st' : i === 1 ? 'nd' : i === 2 ? 'rd' : 'th'}
                      </option>
                    ))}
                  </select>
                  <p className="text-xs text-gray-400 mt-1">For shorter months (e.g., Feb), charges will occur on the last available day</p>
                </div>
              </div>
            )}
          </div>

          {/* Buttons */}
          <div className="flex justify-end space-x-3 pt-4">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 border border-gray-300 rounded-md text-gray-700 hover:bg-gray-50"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={loading}
              className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 disabled:bg-gray-400 disabled:cursor-not-allowed"
            >
              {loading ? 'Creating...' : 'Create Invoice'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default InvoiceModal; 